import React, { useCallback, useMemo, useState } from 'react';
import './ProjectsPreviewPage.scss';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import Loader from '../../../shared/components/common/loader/Loader';
import { PROJECTS } from '../../../shared/routes/path';
import { Trans, useTranslation } from 'react-i18next';
import Title from '../../../shared/components/title/Title';
import PromptsTable from '../../../shared/components/prompts-table/PromptsTable';
import { useFilters } from '../../../shared/contexts/filters/FiltersContext';
import useMonitorPeriodAndChatType from '../../../shared/hooks/projects/preview/UseMonitorPeriodAndChatType';
import useProjectNavigate from '../../../shared/hooks/projects/preview/UseProjectNavigate';
import FilterPanel from '../../../shared/components/filter-panel/FilterPanel';
import MoreBtn from '../../../shared/components/common/more-btn/MoreBtn';
import { Button } from 'primereact/button';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import { Tooltip } from 'primereact/tooltip';
import useExport from '../../../shared/hooks/global/UseExport';
import useChangeName from '../../../shared/hooks/projects/UseChangeName';
import useRemoveProject from '../../../shared/hooks/projects/UseRemoveProject';
import useExportData from '../../../shared/hooks/projects/UseExportData';
import useCanManage from '../../../shared/hooks/account/UseCanManage';
import useChooseKeywords from '../../../shared/hooks/projects/UseChooseKeywords';
import { LinkTrackingTable, ProjectStatsTiles, ShareOfVoiceChart } from './components';
import { useGetTags } from '../../../shared/api/hooks/tags/useGetTags';
import { useGetMonitorsForProject } from '../../../shared/api/hooks/monitor/useGetMonitorsForProject';
import { useGetCommonKeywordsWithTags } from '../../../shared/api/hooks/monitor/keyword/useGetCommonKeywordsWithTags';
import { useGetKeywordsDailyStatsForProject } from '../../../shared/api/hooks/monitor/keyword/stats/useGetKeywordsDailyStatsForProject';
import { useGetSourcesForProject } from '../../../shared/api/hooks/monitor/data/sources/useGetSourcesForProject';
import { ProjectPreviewEmptyState } from './components/empty-state/ProjectPreviewEmptyState';
import { useGetProjectDailyStats } from '../../../shared/api/hooks/project/stats/useGetProjectDailyStats';
import { useGetEvents } from '../../../shared/api/hooks/monitor/events/useGetEvents';
import BrandPositionChart from './components/brand-position-chart/BrandPositionChart';
import { BrandsTable } from './components/brand-table/BrandsTable';
import ShareOfVoiceBarChart from './components/sov-bar-chart/ShareOfVoiceBarChart';
import { useGetProjectAllBrandsStats } from '../../../shared/api/hooks/project/stats/useGetProjectAllBrandsStats';
import { hasProjectStats } from '../../../shared/helpers/project/ProjectHelper';
import { expandSelectedChatTypes } from '../../../shared/helpers/MonitorTypeHelpers';
import { useChartColors } from './hooks/UseChartColors';
import { useKeywordMapper } from './hooks/UseKeywordMapper';
import { usePromptsData } from './hooks/UsePromptsData';
import { useProjectStatsMatrix } from './hooks/UseProjectStatsMatrix';
import { useShareOfVoiceData } from './hooks/UseShareOfVoiceData';
import { useAllModelsStatsMatrix } from './hooks/UseAllModelsStatsMatrix';
import InsightsBox from './components/insights/InsightsBox';
import { useCurrentBrandGeneralStats } from './hooks/UseCurrentBrandGeneralStats';
import { useVisibilityTexts } from './hooks/UseVisibilityTexts';
import { findTagIdInKeyword } from '../../../shared/helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../shared/helpers/tags/TagsHelper';
import { TAG_NAME } from '../../../shared/utils/urls';
import { useGetReport } from '../../../shared/api/hooks/report/useGetReport';
import html2pdf from 'html2pdf.js';
import { PdfReportResponse } from '../../../shared/models/report/PdfReportResponse';
import { useFirstViewTracking } from './hooks/UseFirstViewTracking';
import { useGetProject } from '../../../shared/api/hooks/project/useGetProject';
import ChangeKeywordBox from '../../../shared/components/change-keyword-box/ChangeKeywordBox';
import { useModelsWithoutData } from './hooks/UseModelsWithoutData';
import { useGetAnomalyIncidents } from '../../../shared/api/hooks/anomaly-detection/useGetAnomalyIncidents';
import { usePromptGroups } from '../../../shared/hooks/prompt/UsePromptGroups';
import { PromptGroupItem } from '../../../shared/components/prompts-table/PromptsTable';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { regenerateSuggestedQuestions } from '../../../shared/services/projects/ProjectService';
import useToast from '../../../shared/hooks/global/UseToast';

const ProjectsPreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  let { projectId, type, period, chat } = useParams();
  const projectIdNumber = Number(projectId);

  const { t } = useTranslation();
  const { monitorPeriod, range, selectedChatType, selectedChatTypes } = useFilters();
  const { projectResponse, accountResponse, refetchData, refetchAccountSilently, refetchProjectsSilently } = useAccount();
  const { canManage } = useCanManage();
  const { canExport } = useExport();

  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingBrandScore, setIsDownloadingBrandScore] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regenerateLanguage, setRegenerateLanguage] = useState<{ name: string; type: string } | null>(
    null
  );
  const [isRegenerating, setIsRegenerating] = useState(false);
  const { showToast, toastElement } = useToast();

  const regenerateLanguageOptions = useMemo(
    () => [
      { name: 'PL', type: 'PL' },
      { name: 'EN', type: 'EN' },
    ],
    []
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const hasExplicitModelFilter = searchParams.get('models') !== null;
  const filterChatTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0) return undefined;
    return Array.from(expandSelectedChatTypes(selectedChatTypes));
  }, [hasExplicitModelFilter, selectedChatTypes]);
  const selectedGroupId = useMemo(() => {
    const param = searchParams.get('group');
    if (!param) return -1;
    const parsed = Number(param);
    return isNaN(parsed) ? -1 : parsed;
  }, [searchParams]);
  const setSelectedGroupId = useCallback((groupId: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (groupId === -1) {
        next.delete('group');
      } else {
        next.set('group', String(groupId));
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const hasPromptGroupsAddon = !!accountResponse?.addons?.includes('PROMPT_GROUP');
  const hasAnomalyDetectionAddon = !!accountResponse?.addons?.includes('ANOMALY_DETECTION');
  const canUseAnomaliesForSelection = !hasExplicitModelFilter || selectedChatTypes.length === 1;
  const canShowAnomalyMarkers = hasAnomalyDetectionAddon && monitorPeriod === 'WEEKLY' && canUseAnomaliesForSelection;
  const { data: promptGroups } = usePromptGroups(projectIdNumber);

  const startDate = range?.[0] || null;
  const endDate = range?.[1] || null;

  const {
    project,
    refetch: reloadProject,
    isLoading: isProjectLoading,
  } = useGetProject(projectIdNumber);

  const { refetch: reloadReport } = useGetReport(projectIdNumber, startDate, endDate, period);

  const { tags, refetch: reloadTags, isLoading: isTagsLoading } = useGetTags(projectIdNumber);
  const {
    monitorPrompts,
    isLoading: isMonitorPromptsLoading,
    refetch: reloadMonitorPrompts,
  } = useGetMonitorsForProject(projectIdNumber);

  const filteredMonitorPrompts = useMemo(() => {
    if (!monitorPrompts) return null;
    if (selectedGroupId === -1) return monitorPrompts;
    return monitorPrompts.filter((p) => p.promptGroupId === selectedGroupId);
  }, [monitorPrompts, selectedGroupId]);

  const {
    commonKeywords,
    isLoading: isCommonKeywordsLoading,
    refetch: reloadCommonKeywords,
  } = useGetCommonKeywordsWithTags(projectIdNumber);

  const {
    keywordsDailyStatsForProject,
    isLoading: isKeywordDailyStatsForProjectLoading,
    refetch: reloadKeywordDailyStatsForProject,
  } = useGetKeywordsDailyStatsForProject(
    projectIdNumber,
    selectedChatType,
    monitorPeriod,
    startDate,
    endDate
  );

  const {
    projectDailyStats,
    isLoading: isProjectDailyStatsLoading,
    refetch: reloadProjectDailyStats,
  } = useGetProjectDailyStats(
    projectIdNumber,
    filteredMonitorPrompts,
    monitorPeriod,
    selectedChatType,
    startDate,
    endDate,
    filterChatTypes
  );

  const {
    events,
    isLoading: isEventsLoading,
    refetch: reloadEvents,
  } = useGetEvents(projectIdNumber, monitorPeriod, selectedChatType, startDate, endDate);

  const {
    brandScoreAnomalies,
    averagePositionAnomalies,
    isLoading: isAnomalyIncidentsLoading,
    refetch: reloadAnomalyIncidents,
  } = useGetAnomalyIncidents(
    projectIdNumber,
    startDate,
    endDate,
    new Set(filteredMonitorPrompts ? filteredMonitorPrompts.map(prompt => prompt.keyword) : []),
    filterChatTypes,
    canShowAnomalyMarkers
  );

  const {
    allBrandsStatsResponse,
    isLoading: isAllBrandsStatsResponseLoading,
    refetch: reloadAllBrandsStatsResponse,
  } = useGetProjectAllBrandsStats(projectIdNumber, filteredMonitorPrompts, selectedChatType, monitorPeriod, startDate, endDate, filterChatTypes);

  const { sourcesForProject, refetch: reloadSourcesForProject } = useGetSourcesForProject(
    projectIdNumber,
    filteredMonitorPrompts,
    selectedChatType,
    startDate,
    endDate
  );

  const onRefresh = useCallback(async () => {
    await Promise.all([
      reloadProject(),
      reloadMonitorPrompts(),
      reloadTags(),
      reloadCommonKeywords(),
      reloadKeywordDailyStatsForProject(),
      reloadProjectDailyStats(),
      reloadEvents(),
      reloadAnomalyIncidents(),
      reloadAllBrandsStatsResponse(),
      reloadSourcesForProject(),
      refetchAccountSilently(),
      refetchProjectsSilently(),
    ]);
  }, [
    reloadProject,
    reloadMonitorPrompts,
    reloadTags,
    reloadCommonKeywords,
    reloadKeywordDailyStatsForProject,
    reloadProjectDailyStats,
    reloadEvents,
    reloadAnomalyIncidents,
    reloadAllBrandsStatsResponse,
    reloadSourcesForProject,
    refetchAccountSilently,
    refetchProjectsSilently,
  ]);

  const { confirmNewProjectKeywords } = useChooseKeywords({
    refreshProjects: onRefresh,
  });

  const { editName } = useChangeName({ onRefresh });

  const { confirmRemove } = useRemoveProject({
    refreshProjects: () => {
      refetchData();
      navigate(PROJECTS);
    },
  });

  const downloadReport = async () => {
    const result = await reloadReport();
    if (!result || result.status !== 'success' || !result.data) {
      return;
    }

    const data = result.data as PdfReportResponse;
    let template = data.template as string;

    (data.chartAttachments || []).forEach((attachment: string) => {
      const [filename, base64] = attachment.split(': ');
      if (!filename || !base64) return;
      const cid = `cid:${filename.trim()}`;
      template = template.split(cid).join(`data:image/png;base64,${base64.trim()}`);
    });

    const parser = new DOMParser();
    const doc = parser.parseFromString(template, 'text/html');

    const images = doc.querySelectorAll('img');
    images.forEach((img) => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      img.style.margin = '0 auto';
    });

    template = doc.body.innerHTML;

    const styledTemplate = `
    <div style="
      width: 190mm; 
      max-width: 190mm; 
      margin: 0 auto; 
      padding: 10mm;
      box-sizing: border-box;
      font-family: Arial, sans-serif;
    ">
      ${template}
    </div>
  `;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = styledTemplate;

    await html2pdf()
      .from(wrapper)
      .set({
        filename: 'report.pdf',
        html2canvas: {
          scale: 2,
          useCORS: true,
          scrollY: 0,
          scrollX: 0,
          windowWidth: 1200,
          letterRendering: true,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
        },
        pagebreak: {
          mode: ['avoid-all', 'css'],
          avoid: '.report-section',
        },
      } as any)
      .save();
  };

  useProjectNavigate(projectIdNumber);
  useMonitorPeriodAndChatType(type, period, chat, projectIdNumber, accountResponse);
  const { exportStats, exportSources, exportBrandScore } = useExportData(projectIdNumber);

  const firmName = useMemo(() => {
    if (!project) {
      return '';
    }

    return project.firmName || '';
  }, [project]);

  const firmNameOrg = useMemo(() => {
    if (!firmName || !project) {
      return '';
    }

    const tagId = commonKeywords ? findTagIdInKeyword(firmName, commonKeywords) : null;
    return tags ? getTagLabel(tags, tagId, project.firmNameOrg) : project.firmNameOrg;
  }, [project, tags, commonKeywords, firmName]);

  const hasAnyPendingQuestions = !!monitorPrompts?.some(
    (promptData) => !['ACTIVE', 'PAUSED'].includes(promptData.status)
  );

  const nonEmptyPromptGroups = useMemo(() => {
    if (!promptGroups || !monitorPrompts) return [];
    const groupIdsWithPrompts = new Set(
      monitorPrompts
        .map((p) => p.promptGroupId)
        .filter((id): id is number => id != null)
    );
    return promptGroups.filter((g) => groupIdsWithPrompts.has(g.id));
  }, [promptGroups, monitorPrompts]);

  const groupFilterOptions = useMemo(() => {
    if (!hasPromptGroupsAddon || nonEmptyPromptGroups.length === 0) return [];
    return [
      { label: t('allGroups'), value: -1 },
      ...nonEmptyPromptGroups.map((g) => ({ label: g.name, value: g.id })),
    ];
  }, [hasPromptGroupsAddon, nonEmptyPromptGroups, t]);

  const filteredKeywordsDailyStats = useMemo(() => {
    if (!filteredMonitorPrompts || selectedGroupId === -1) return keywordsDailyStatsForProject;
    const filteredIds = new Set(filteredMonitorPrompts.map((p) => p.id).filter((id): id is number => id != null));
    return {
      ...keywordsDailyStatsForProject,
      monitorPromptsKeywordsStatsResponse: keywordsDailyStatsForProject.monitorPromptsKeywordsStatsResponse.filter(
        (entry) => filteredIds.has(entry.monitorId)
      ),
    };
  }, [keywordsDailyStatsForProject, filteredMonitorPrompts, selectedGroupId]);

  const keywordsMap = useKeywordMapper(commonKeywords, monitorPrompts);
  const firmNameLabel = useMemo(() => {
    // For tag-linked brands (firmName starts with TAG_NAME prefix), keywordsMap
    // maps the raw tag key to itself (chatbeat_tag_name_X → chatbeat_tag_name_X),
    // which is not a useful display label. Skip the map lookup for tag prefixes
    // and go directly to getTagLabel, mirroring exactly what getPieChartWithOthers does.
    if (!firmName.startsWith(TAG_NAME)) {
      const mappedLabel = keywordsMap.get(firmName);
      if (mappedLabel) return mappedLabel;
    }

    // firmName is a tag-prefixed keyword (chatbeat_tag_name_<tagId>).
    // Resolve the same way getPieChartWithOthers does so that firmNameLabel
    // matches the display label stored in shareOfVoiceData slice names.
    const tagId = commonKeywords ? findTagIdInKeyword(firmName, commonKeywords) : null;
    if (tagId && tags) {
      return getTagLabel(tags, tagId, firmName);
    }

    return firmName;
  }, [keywordsMap, firmName, commonKeywords, tags]);

  const shareOfVoiceData = useShareOfVoiceData(
    allBrandsStatsResponse,
    keywordsMap,
    firmName,
    commonKeywords,
    tags
  );
  const promptsData = usePromptsData(
    isKeywordDailyStatsForProjectLoading,
    isEventsLoading,
    keywordsDailyStatsForProject,
    keywordsMap,
    filteredMonitorPrompts,
    selectedChatType,
    accountResponse,
    commonKeywords,
    tags,
    selectedChatTypes
  );
  const projectStatsMatrix = useProjectStatsMatrix(
    isProjectDailyStatsLoading,
    isMonitorPromptsLoading,
    isEventsLoading,
    period,
    projectDailyStats
  );
  const allModelsStatsMatrix = useAllModelsStatsMatrix(
    isProjectDailyStatsLoading,
    isMonitorPromptsLoading,
    isEventsLoading,
    filteredKeywordsDailyStats,
    firmName,
    accountResponse,
    shareOfVoiceData,
    keywordsMap,
    hasExplicitModelFilter,
    selectedChatTypes
  );
  const modelsWithoutData = useModelsWithoutData(
    accountResponse,
    keywordsDailyStatsForProject,
    isKeywordDailyStatsForProjectLoading,
    range
  );
  const brandColors = useChartColors(shareOfVoiceData, firmNameLabel, keywordsMap);
  const currentBrandGeneralStats = useCurrentBrandGeneralStats(firmName, allBrandsStatsResponse);
  const monitorHeaders = [t('brandScore'), t('averagePosition')];
  const visibilityTexts = useVisibilityTexts(
    firmName,
    firmNameLabel,
    currentBrandGeneralStats,
    promptsData,
    selectedChatType,
    accountResponse,
    allModelsStatsMatrix.matrix,
    tags,
    commonKeywords
  );

  const handleExport = async () => {
    if (isDownloading || !canExport) {
      return;
    }

    try {
      setIsDownloading(true);
      await exportStats();
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDailyStatsExport = async () => {
    if (isDownloadingBrandScore || !canExport) {
      return;
    }

    try {
      setIsDownloadingBrandScore(true);
      await exportBrandScore(monitorPeriod);
    } finally {
      setIsDownloadingBrandScore(false);
    }
  };

  const handleDownload = async () => {
    if (isDownloadingReport) {
      return;
    }

    try {
      setIsDownloadingReport(true);
      await downloadReport();
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const isProjectDataReady = useMemo(() => {
    if (isProjectDailyStatsLoading || isMonitorPromptsLoading || isAllBrandsStatsResponseLoading) {
      return false;
    }

    return (
      !!allBrandsStatsResponse &&
      allBrandsStatsResponse.length > 0 &&
      !!monitorPrompts &&
      monitorPrompts.length > 0 &&
      !hasAnyPendingQuestions
    );
  }, [
    allBrandsStatsResponse,
    monitorPrompts,
    hasAnyPendingQuestions,
    isProjectDailyStatsLoading,
    isMonitorPromptsLoading,
    isAllBrandsStatsResponseLoading,
  ]);

  useFirstViewTracking({
    projectId: projectIdNumber,
    accountResponse,
    isDataReady: isProjectDataReady,
  });

  if (!accountResponse || !projectResponse || !project) {
    return <></>;
  }

  if (!isMonitorPromptsLoading && monitorPrompts && monitorPrompts.length === 0) {
    return (
      <ProjectPreviewEmptyState
        firmNameOrg={firmNameOrg}
        isSuperAdmin={accountResponse.isSuperAdmin}
        currentProject={project || null}
        projectId={projectId}
        demoVersion={project?.demoVersion || null}
        accountStatus={accountResponse.accountStatus}
        canManage={!!canManage}
      />
    );
  }

  if (isMonitorPromptsLoading || isTagsLoading || !projectResponse || !accountResponse) {
    return (
      <div className=" page-content">
        <div className="page-box">
          <Loader />
        </div>
      </div>
    );
  }

  if (!projectIdNumber) {
    navigate(PROJECTS);
    return null;
  }

  const isAllSelected = selectedChatType === 'ALL';
  const isFiltersReady = !!selectedChatType && !!monitorPeriod;
  const hasProjectStatsData = hasProjectStats(projectStatsMatrix);

  const isShareOfVoiceLoading =
    !isFiltersReady ||
    isProjectDailyStatsLoading ||
    isMonitorPromptsLoading ||
    isAllBrandsStatsResponseLoading ||
    isEventsLoading;

  return (
    <>
      <div className="preview-project-page page-content">
        <div className="page-box">
          <div className="page-box__filters" data-observe-sticky>
            <Title
              isBaseCategory={false}
              title={firmNameOrg}
              titleStyle={`ico--md  ${
                project.localization ? `fi-${project.localization.toLowerCase()}` : 'ico--globe'
              } `}
              children={
                <div className="filters">
                  {!project.demoVersion && <ChangeKeywordBox project={project} changeKeyword={onRefresh} />}
                  <FilterPanel baseUrl={location.pathname}>
                    {groupFilterOptions.length > 0 && (
                      <Dropdown
                        value={selectedGroupId}
                        onChange={(e) => setSelectedGroupId(e.value)}
                        options={groupFilterOptions}
                        optionLabel="label"
                        optionValue="value"
                        className="dropdown dropdown--group-filter"
                        valueTemplate={(option) => (
                          <>
                            <span id={`group-filter-val-${option?.value}`} className="dropdown--group-filter__label">
                              {option?.label}
                            </span>
                            {option?.label && (
                              <Tooltip target={`#group-filter-val-${option.value}`} content={option.label} position="top" showDelay={300} />
                            )}
                          </>
                        )}
                        itemTemplate={(option) => (
                          <>
                            <span id={`group-filter-item-${option.value}`} className="dropdown--group-filter__label">
                              {option.label}
                            </span>
                            <Tooltip target={`#group-filter-item-${option.value}`} content={option.label} position="top" showDelay={300} />
                          </>
                        )}
                      />
                    )}
                  </FilterPanel>
                  <MoreBtn rowId={0}>
                    <div className="buttons-list">
                      {!project?.demoVersion &&
                        !['BLOCKED', 'DISABLED'].includes(accountResponse?.accountStatus) && (
                          <>
                            <Button
                              id={`btn-edit-project-${projectIdNumber}`}
                              className="action-btn material-symbols-outlined"
                              onClick={() => editName(projectIdNumber)}
                            >
                              <span className="ico--edit ico--xs" />
                            </Button>
                            <CustomTooltip
                              tooltipText="editProject"
                              targetRef={`btn-edit-project-${projectIdNumber}`}
                            />
                            <Button
                              id={`btn-download-stats-${projectIdNumber}`}
                              className={`action-btn material-symbols-outlined ${
                                !canExport ? 'btn--disabled-tooltip' : ''
                              }`}
                              onClick={handleExport}
                            >
                              {isDownloading ? (
                                <span className="ico--loader ico--xs" />
                              ) : (
                                <span
                                  className={`ico--download ${
                                    !canExport ? 'ico--disabled' : ''
                                  } ico--xs`}
                                />
                              )}
                            </Button>
                            <CustomTooltip
                              tooltipText={
                                !canExport ? 'upgradePlanTrialExportMsg' : 'downloadPromptStats'
                              }
                              targetRef={`btn-download-stats-${projectIdNumber}`}
                            />
                            <Button
                              id={`btn-download-brand-score-${projectIdNumber}`}
                              className={`action-btn material-symbols-outlined ${
                                !canExport ? 'btn--disabled-tooltip' : ''
                              }`}
                              onClick={handleDailyStatsExport}
                            >
                              {isDownloadingBrandScore ? (
                                <span className="ico--loader ico--xs" />
                              ) : (
                                <span className={`ico--download ico--xs`} />
                              )}
                            </Button>
                            <CustomTooltip
                              tooltipText={
                                !canExport ? 'upgradePlanTrialExportMsg' : 'downloadBrandScore'
                              }
                              targetRef={`btn-download-brand-score-${projectIdNumber}`}
                            />
                          </>
                        )}
                      {accountResponse?.addons.includes('REPORTS') && (
                        <>
                          <Button
                            id={`btn-pdf-report-${projectIdNumber}`}
                            className="action-btn material-symbols-outlined"
                            onClick={handleDownload}
                          >
                            {isDownloadingReport ? (
                              <span className="ico--loader ico--xs" />
                            ) : (
                              <span className="ico--report ico--xs" />
                            )}
                          </Button>
                          <CustomTooltip
                            tooltipText="downloadPdfReport"
                            targetRef={`btn-pdf-report-${projectIdNumber}`}
                          />
                        </>
                      )}
                      {accountResponse?.isSuperAdmin && !accountResponse?.isLimitedSuperAdmin && (
                        <>
                          <Button
                            id={`btn-regen-suggested-${projectIdNumber}`}
                            className="action-btn material-symbols-outlined"
                            onClick={() => setRegenerateDialogOpen(true)}
                            disabled={isRegenerating}
                          >
                            <span className="ico--regenerate ico--xs" />
                          </Button>
                          <CustomTooltip
                            tooltipText="regenerateSuggestedPrompts"
                            targetRef={`btn-regen-suggested-${projectIdNumber}`}
                          />
                        </>
                      )}
                      <Button
                        id={`btn-remove-project-${projectIdNumber}`}
                        className="action-btn material-symbols-outlined"
                        onClick={() => confirmRemove(projectIdNumber)}
                      >
                        <span className="ico--trash ico--xs" />
                      </Button>
                      <CustomTooltip
                        tooltipText="removeProject"
                        targetRef={`btn-remove-project-${projectIdNumber}`}
                      />
                    </div>
                  </MoreBtn>
                </div>
              }
            />
          </div>

          <>
            {hasAnyPendingQuestions && (
              <div className="data-collecting-notice">
                <div className="status status--pending">
                  <div className="status__ring" />
                  <div className="status__dot" />
                </div>
                <span className="data-collecting-notice__text">{t('dataCollectingNotice')}</span>
              </div>
            )}

            <div className="page-box__stats page-box__stats--without-bottom-padding">
              <ProjectStatsTiles
                projectGeneralData={
                  allBrandsStatsResponse.find((item) => item.keyword === firmName) || null
                }
                isLoading={
                  !isFiltersReady ||
                  isProjectDailyStatsLoading ||
                  isMonitorPromptsLoading ||
                  (!hasProjectStatsData && hasAnyPendingQuestions)
                }
              />

              <div className="stats">
                <BrandPositionChart
                  averagePositionData={projectStatsMatrix?.secondMatrix ?? []}
                  brandScoreData={projectStatsMatrix?.matrix ?? []}
                  monitorHeaders={monitorHeaders}
                  eventsData={events}
                  hasAnyPendingQuestions={hasAnyPendingQuestions}
                  hasProjectStatsData={hasProjectStatsData}
                  isLoading={
                    !isFiltersReady ||
                    isProjectDailyStatsLoading ||
                    isMonitorPromptsLoading ||
                    isEventsLoading ||
                    (canShowAnomalyMarkers && isAnomalyIncidentsLoading)
                  }
                  averagePositionAnomalies={averagePositionAnomalies}
                  brandScoreAnomalies={brandScoreAnomalies}
                  period={monitorPeriod}
                />
                <ShareOfVoiceChart
                  key={`share-of-voice-chart-${projectId}`}
                  shareOfVoiceData={shareOfVoiceData}
                  hasAnyPendingQuestions={hasAnyPendingQuestions}
                  firmName={firmNameLabel}
                  hasProjectStatsData={hasProjectStatsData}
                  onSettingsClick={() =>
                    confirmNewProjectKeywords(
                      projectIdNumber,
                      () => project.firmName,
                      commonKeywords || [],
                      tags
                    )
                  }
                  isLoading={isShareOfVoiceLoading}
                />
              </div>

              {isAllSelected &&
                !isCommonKeywordsLoading &&
                allModelsStatsMatrix.matrix &&
                allModelsStatsMatrix.matrix.length > 0 &&
                brandColors &&
                brandColors.length > 0 && (
                  <div className="stats stats--scroll stats--full-width">
                    <ShareOfVoiceBarChart
                      allModelsStatsMatrix={allModelsStatsMatrix.matrix}
                      brandColors={brandColors}
                      firmName={firmNameLabel}
                      keywordsMapper={keywordsMap}
                      commonKeywords={commonKeywords}
                      tags={tags}
                    />
                  </div>
                )}

              {!isShareOfVoiceLoading &&
                !isCommonKeywordsLoading &&
                allBrandsStatsResponse &&
                allBrandsStatsResponse.length > 0 &&
                accountResponse.addons.includes('TABLE') && (
                  <div className="stats">
                    <BrandsTable
                      allBrandsStatsResponse={allBrandsStatsResponse}
                      keywordsMapper={keywordsMap}
                      commonKeywords={commonKeywords}
                      tags={tags}
                    />
                    <InsightsBox
                      hasAnyPendingQuestions={hasAnyPendingQuestions}
                      visibilityTexts={visibilityTexts}
                    />
                  </div>
                )}
            </div>

            <div id="key-prompts">
              <PromptsTable
                tableTitle={`${t('popularQuestionsFor')} ${firmNameOrg}`}
                data={promptsData}
                refreshAfterDelete={onRefresh}
                range={range}
                showKeyword={false}
                projectId={projectIdNumber}
                chatType={selectedChatType ?? 'GPT'}
                periodType={monitorPeriod ?? null}
                isLoading={!isFiltersReady || isKeywordDailyStatsForProjectLoading}
                modelsWithoutData={modelsWithoutData}
                selectedChatTypes={selectedChatTypes}
                additionalClass={
                  sourcesForProject.linksData && sourcesForProject.linksData.length > 0
                    ? 'table-content--without-bottom'
                    : ''
                }
                groups={hasPromptGroupsAddon
                  ? selectedGroupId === -1
                    ? (nonEmptyPromptGroups as PromptGroupItem[])
                    : (nonEmptyPromptGroups as PromptGroupItem[]).filter((g) => g.id === selectedGroupId)
                  : undefined
                }
                hasPromptGroupsAddon={hasPromptGroupsAddon}
                groupDisplayMode={hasPromptGroupsAddon ? 'accordion' : undefined}
              />
            </div>

            {!isProjectDailyStatsLoading &&
              !isMonitorPromptsLoading &&
              sourcesForProject.linksData &&
              sourcesForProject.linksData?.length > 0 && (
                <LinkTrackingTable
                  exportSources={exportSources}
                  domains={sourcesForProject.domains || []}
                  sources={sourcesForProject.linksData || []}
                  isAllSelected={isAllSelected}
                  isDemo={!!project.demoVersion}
                />
              )}
          </>
        </div>
      </div>
      {toastElement}
      <Dialog
        header={t('regenerateSuggestedPrompts')}
        visible={regenerateDialogOpen}
        onHide={() => !isRegenerating && setRegenerateDialogOpen(false)}
        className="dialog-box regenerate-prompts-dialog"
        style={{ width: '460px' }}
        dismissableMask={!isRegenerating}
      >
        <div className="regenerate-prompts-dialog__content">
          <p className="regenerate-prompts-dialog__desc">
            <Trans i18nKey="regenerateSuggestedPromptsDesc" components={{ bold: <strong /> }} />
          </p>
          <Dropdown
            value={regenerateLanguage}
            onChange={(e) => setRegenerateLanguage(e.value)}
            options={regenerateLanguageOptions}
            optionLabel="name"
            placeholder={t('forceLanguageOptional')}
            className="dropdown"
            showClear
          />
          <div className="regenerate-prompts-dialog__footer">
            <Button
              className="btn btn--sm btn--secondary"
              label={t('cancel')}
              onClick={() => setRegenerateDialogOpen(false)}
              disabled={isRegenerating}
            />
            <Button
              className="btn btn--sm"
              label={isRegenerating ? t('regenerating') : t('regenerate')}
              disabled={isRegenerating}
              onClick={async () => {
                if (isRegenerating) return;
                setIsRegenerating(true);
                try {
                  const result = await regenerateSuggestedQuestions(
                    projectIdNumber,
                    regenerateLanguage?.type ?? null
                  );
                  showToast(
                    t('regenerateSingleSuccess', { deleted: result.deletedCount }),
                    'success'
                  );
                  setRegenerateDialogOpen(false);
                  setRegenerateLanguage(null);
                } catch (err) {
                  console.error('Regenerate failed', err);
                  showToast(t('regenerateError'), 'error');
                } finally {
                  setIsRegenerating(false);
                }
              }}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ProjectsPreviewPage;
